import React, { useRef, useState } from "react";
import classNames from "classnames";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { StickyDataTable } from "@design-system/DataTable/StickyDataTable";
import { BooleanControl, VariantGrid, VariantItem } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import previewStyles from "./DatePickerPlayground.module.scss";
import styles from "./TablesPlayground.module.scss";

const accountRows = [
    {
        id: "checking",
        account: "Operating checking",
        institution: "Chase",
        type: "Bank",
        january: "$24,820",
        february: "$28,410",
        march: "$31,095",
        april: "$29,760",
        balance: "$29,760",
    },
    {
        id: "savings",
        account: "Tax reserve",
        institution: "Mercury",
        type: "Bank",
        january: "$8,500",
        february: "$10,250",
        march: "$12,000",
        april: "$14,800",
        balance: "$14,800",
    },
    {
        id: "stripe",
        account: "Stripe clearing",
        institution: "Stripe",
        type: "Processor",
        january: "$1,240",
        february: "$980",
        march: "$1,625",
        april: "$1,410",
        balance: "$1,410",
    },
    {
        id: "amex",
        account: "Amex business card",
        institution: "American Express",
        type: "Credit card",
        january: "($2,180)",
        february: "($3,420)",
        march: "($2,765)",
        april: "($3,110)",
        balance: "($3,110)",
    },
];

const columns = [
    "Account",
    "Institution",
    "Type",
    "January",
    "February",
    "March",
    "April",
    "Current balance",
];

export const StickyDataTablePlayground = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasStickyLeft, setHasStickyLeft] = useState(true);
    const [hasStickyRight, setHasStickyRight] = useState(true);
    const [isAlwaysSticky, setIsAlwaysSticky] = useState(false);
    const [isStickingRight, setIsStickingRight] = useState(true);

    return (
        <SandboxLayout
            controls={
                <section>
                    <h2>Sticky behavior</h2>
                    <div className={previewStyles.booleanGrid}>
                        <BooleanControl
                            label="Sticky first column"
                            checked={hasStickyLeft}
                            onChange={setHasStickyLeft}
                        />
                        <BooleanControl
                            label="Sticky last column"
                            checked={hasStickyRight}
                            onChange={setHasStickyRight}
                        />
                        <BooleanControl
                            label="Always show sticky shadows"
                            checked={isAlwaysSticky}
                            onChange={setIsAlwaysSticky}
                        />
                    </div>
                </section>
            }
        >
            <ShowcasePreview
                summary="Scroll horizontally to pin edge columns"
                width="full"
                output={JSON.stringify(
                    {
                        StickyDataTable: {
                            stickyLeft: hasStickyLeft,
                            stickyRight: hasStickyRight,
                            alwaysHorizontalSticky: isAlwaysSticky,
                        },
                        showcaseState: { isStickingRight },
                    },
                    null,
                    2,
                )}
            >
                <VariantGrid title="Scrollable accounts" columns="full">
                    <VariantItem label="StickyDataTable">
                        <div
                            ref={containerRef}
                            className={classNames(
                                "data-table",
                                "data-table--with-border",
                                styles.stickyDataTable,
                            )}
                        >
                            <StickyDataTable
                                containerRef={containerRef}
                                stickyLeft={hasStickyLeft}
                                stickyRight={hasStickyRight}
                                alwaysHorizontalSticky={isAlwaysSticky}
                                setIsStickingRight={setIsStickingRight}
                            >
                                <table
                                    className={classNames(
                                        "data-table__table",
                                        "data-table__table--auto-column-width",
                                        styles.scrollTable,
                                    )}
                                >
                                    <thead>
                                        <tr>
                                            {columns.map((column, index) => (
                                                <th
                                                    key={column}
                                                    className={classNames(
                                                        "data-table__header-cell",
                                                        index === 0
                                                            ? "data-table__header-cell--title data-table__header-cell--string-col"
                                                            : undefined,
                                                    )}
                                                >
                                                    {column}
                                                    <span
                                                        aria-hidden
                                                        className="data-table__cover-cell"
                                                    >
                                                        {column}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accountRows.map((account) => (
                                            <tr
                                                key={account.id}
                                                className="data-table__row"
                                            >
                                                <td className="data-table__cell data-table__cell--title">
                                                    {account.account}
                                                </td>
                                                <td className="data-table__cell data-table__cell--string-col">
                                                    {account.institution}
                                                </td>
                                                <td className="data-table__cell data-table__cell--string-col">
                                                    {account.type}
                                                </td>
                                                <td className="data-table__cell">
                                                    {account.january}
                                                </td>
                                                <td className="data-table__cell">
                                                    {account.february}
                                                </td>
                                                <td className="data-table__cell">
                                                    {account.march}
                                                </td>
                                                <td className="data-table__cell">
                                                    {account.april}
                                                </td>
                                                <td className="data-table__cell data-table__cell--bold">
                                                    {account.balance}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </StickyDataTable>
                        </div>
                    </VariantItem>
                </VariantGrid>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
